import "@tento-ui/components/prosemirror";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { ProseMirrorEditor } from "@tento-ui/components/prosemirror";
import { Shimmer } from "@tento-ui/components/shimmer";
import { Scrollbars } from "@tento-ui/components/scrollbars";
import { TextCellPopup } from "./text-cell-popup.ts";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { type Option, RowId } from "@tento-lona/sheets";
import type { RowLocalId } from "@tento-lona/sheets";
import { CellHandle } from "@tento-lona";
import { SheetDelegate } from "../../../../sheet/sheet-delegate.ts";
import { CellExpander } from "../../../shared/cell-expand/cell-expander.ts";
import { LAZY_EDITOR_MOUNTS, LazyEditorMount } from "@tento-ui/components/sheet/lazy-editor-mount";

const TEXT_CELL_SCROLL_OVERFLOW_TOLERANCE_PX = 1;
const TEXT_CELL_EXPAND_LENGTH_FALLBACK = 80;

/** Whether `$el` sits in a column parked in the scroll pool (`[pooled]`, `scroll-column-retention`). */
function parkedInPool($el: Element): boolean {
  let node: Node | null = $el;
  while (node != null) {
    if (node instanceof Element && node.hasAttribute("pooled")) return true;
    node = node.parentNode ?? (node instanceof ShadowRoot ? node.host : null);
  }
  return false;
}

@component({ name: "std-text-cell" })
export class TextCell extends LonaWebComponent {
  #$editor: ProseMirrorEditor = this.hydrate("editor");
  #$root: HTMLElement = this.$("root");
  #$expandBtn: HTMLElement = this.$("expand-btn");

  #handle: Option<SheetDelegate> = null;
  #position: Option<{
    column: ColumnIndex.Key.Resolved.Default;
    rowId: RowId;
    localId: RowLocalId;
  }> = null;
  #currentOnUpdate: Option<(md: string) => void> = null;
  #inThePast: boolean = false;
  #editable: boolean = true;
  /** Live data subscription for the currently-bound cell; disposed when the
   *  cell is rebound to a different (row, week) or recycled. */
  #cellHandle: Option<CellHandle> = null;
  /** Last-bound `(rowId, week)` key — lets the plugin detect cross-week reuse
   *  of this pooled cell without a plugin-side Map. */
  #boundKey: Option<string> = null;

  /** Owns the expand button, x-axis-clip detection, and the full-width popup.
   *  Content here is the ProseMirror editor (editable inherited). */
  #expander: CellExpander = new CellExpander({
    host: this,
    $expandBtn: this.#$expandBtn,
    getAnchor: () => this.#anchor(),
    isClipped: () => this.#isClipped(),
    makePopup: (ctx) => this.#makePopup(ctx),
    popupIdPrefix: "text-popup",
  });

  constructor() {
    super();
    // `calendar-lazy-editor-mount`: hovering a static cell on a still
    // timeline builds its editor before any click lands (pointer-down and
    // focus build it in every case, see ProseMirrorEditor.activate).
    this.addEventListener("pointerenter", () => {
      if (!LazyEditorMount.enabled() || !this.#$editor.isStatic || !LAZY_EDITOR_MOUNTS.still()) return;
      LAZY_EDITOR_MOUNTS.delete(this.#mountTarget);
      this.#$editor.activate("pointer");
    });
  }

  #anchor(): Option<CellExpander.Anchor> {
    if (this.#handle == null || this.#position == null) return null;
    return {
      handle: this.#handle,
      column: this.#position.column,
      rowId: this.#position.rowId,
      localId: this.#position.localId,
    };
  }

  #isClipped(): boolean {
    const value = this.value;
    if (value == null || value.trim().length === 0) return false;
    return (
      this.#$root.scrollWidth - this.#$root.clientWidth >
        TEXT_CELL_SCROLL_OVERFLOW_TOLERANCE_PX ||
      this.#$editor.hasContentOverflow() ||
      TextCell.markdownMayNeedExpansion(value)
    );
  }

  /** Re-check expandability — called by the plugin's `onResize`. */
  refreshExpandState(): void {
    this.#expander.refreshAfterResize();
  }

  /**
   * `calendar-lazy-editor-mount`: the static cell's editor is
   * also built when the pointer enters the cell, and in idle time once the
   * timeline is still (`LazyEditorMount`), so a fling mounts no editor in its
   * frames while a settled sheet ends up with live editors as before.
   */
  readonly #mountTarget: LazyEditorMount.Target = {
    mountState: () => {
      if (!this.isConnected || !this.#$editor.isStatic) return "gone";
      return parkedInPool(this) ? "hidden" : "ready";
    },
    mount: () => this.#$editor.activate("pointer"),
  };

  #mountWhenStill(): void {
    if (!LazyEditorMount.enabled()) return;
    if (this.#$editor.isStatic && this.#editable) LAZY_EDITOR_MOUNTS.add(this.#mountTarget);
    else LAZY_EDITOR_MOUNTS.delete(this.#mountTarget);
  }

  async #bindInlineEditor(props: ProseMirrorEditor.Props): Promise<void> {
    await this.#$editor.bind({
      // Static markup until reached or idle (`LazyEditorMount`); off, the editor is live from the bind.
      lazy: LazyEditorMount.enabled(),
      ...props,
      onUpdate: (md) => {
        props.onUpdate?.(md);
        this.#expander.refresh();
      },
    });
    this.#mountWhenStill();
  }

  async bind(props: TextCell.Props): Promise<this> {
    this.#handle = props.handle ?? null;
    this.#position =
      props.column && props.rowId && props.localId
        ? { column: props.column, rowId: props.rowId, localId: props.localId }
        : null;
    this.#currentOnUpdate = props.onUpdate ?? null;
    this.#inThePast = props.inThePast ?? false;

    // Toggle loading state
    this.toggleAttribute("loading", props.loading ?? false);
    if (props.loading) {
      this.#expander.onRebind();
      return this;
    }

    this.#editable = props.editable !== false;
    await this.#bindInlineEditor(props);
    this.#expander.onRebind();

    return this;
  }

  async #makePopup(
    ctx: CellExpander.PopupContext,
  ): Promise<CellExpander.Popup> {
    return await TextCellPopup.make(ctx, {
      inThePast: this.#inThePast,
      editable: this.#editable,
      markdown: this.value,
      onUpdate: (md) => {
        void this.#bindInlineEditor({
          markdown: md,
          editable: this.#editable,
          onUpdate: this.#currentOnUpdate,
        });
        this.#currentOnUpdate?.(md);
        this.#expander.refresh();
      },
    });
  }

  get value(): Option<string> {
    return this.#$editor.value ?? null;
  }

  destroyEditor(): void {
    LAZY_EDITOR_MOUNTS.delete(this.#mountTarget);
    this.#$editor.onRecycle();
  }

  /** Adopt a new data subscription, disposing the previous one. */
  setCellHandle(h: Option<CellHandle>): void {
    this.#cellHandle?.dispose();
    this.#cellHandle = h;
  }

  /** Record this cell's `(row, week)` identity; returns true if it changed
   *  since the last bind (i.e. the pooled cell was reused for another week). */
  adoptIdentity(key: string): boolean {
    const changed = this.#boundKey !== key;
    this.#boundKey = key;
    return changed;
  }

  focusEditor(): void {
    this.#$editor.focusEditor();
  }

  // Recycle clears DOM but not JS fields (see learnings 2026-02-15 /
  // 2026-02-17). Null the persist target (`currentOnUpdate`) and `position` so
  // a pooled cell can't carry a previous week's persist callback into the
  // expand-popup path, and reset the editor itself.
  onRecycle(): void {
    super.onRecycle();
    this.#expander.onRecycle();
    this.#cellHandle?.dispose();
    this.#cellHandle = null;
    this.#boundKey = null;
    this.#handle = null;
    this.#position = null;
    this.#currentOnUpdate = null;
    this.#inThePast = false;
    this.#editable = true;
    LAZY_EDITOR_MOUNTS.delete(this.#mountTarget);
    this.#$editor.onRecycle();
  }

  static $styles: Style.Compat[] = [
    Scrollbars.$hideScrollbars,
    Shimmer.$cellLoading,
    css`
      :host {
        display: flex;
        position: relative;
        width: 100%;
        height: 100%;
        cursor: text;
      }

      #root {
        display: flex;
        overflow-x: hidden;
        height: 100%;
        width: 100%;
        cursor: text;
        --editor-overflow-x: hidden;
      }

      #root:focus-within {
        overflow-x: scroll;
        --editor-overflow-x: scroll;
      }

      #editor {
        display: block;
        height: 100%;
        width: 100%;
        --editor-padding: 10px;
        flex-grow: 1;
      }

      :host([expandable]) #editor {
        --editor-padding: 10px 10px 28px 10px;
      }

      #expand-btn {
        position: absolute;
        bottom: 4px;
        right: 4px;
        width: 20px;
        height: 20px;
        display: grid;
        place-items: center;
        border-radius: 4px;
        cursor: pointer;
        z-index: 1;
        background: var(--bg-hover-opaque-very-light);
        color: var(--fg-icon-pri);
      }

      #expand-btn > svg {
        width: 12px;
        height: 12px;
      }

      #expand-btn:hover {
        background: var(--bg-hover-opaque);
      }

      #expand-btn[hidden] {
        display: none;
      }

      :host([loading]) #editor {
        display: none;
      }
    `,
  ];

  static $html: HTMLTemplateElement = html`
    <div id=root>
      <std-prosemirror id="editor"></std-prosemirror>
      <div id="expand-btn" hidden></div>
    </div>
  `;
}

export namespace TextCell {
  export function markdownMayNeedExpansion(markdown: string): boolean {
    return (
      markdown.length > TEXT_CELL_EXPAND_LENGTH_FALLBACK ||
      markdown.includes("\n")
    );
  }

  export type Props = ProseMirrorEditor.Props & {
    handle?: SheetDelegate;
    column?: ColumnIndex.Key.Resolved.Default;
    rowId?: RowId;
    localId?: RowLocalId;
    widthPx?: number;
    inThePast?: boolean;
    loading?: boolean;
  };
}
